import React, { useMemo, useState } from "react";
import { useGSheet } from "@/hooks/useGSheet";
import { LiveHeader, StateBlock, Badge, crc, cell } from "@/components/live/LiveShared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Leaf, Search } from "lucide-react";

const buildName = (it) =>
  [it.genero, it.especie, it.cultivar, it.variegacion, it["tamaño"]]
    .map((x) => String(x || "").trim())
    .filter((x) => x && x.toLowerCase() !== "no")
    .join(" ")
    .trim() || it.product_id;

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isAvailable = (status) => normalize(status) === "disponible";
const isUnavailable = (status) => {
  const s = normalize(status);
  return s === "no disponible" || s === "perdida";
};

export default function LiveCatalogPage() {
  const { data, loading, error, refresh } = useGSheet("catalog");
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState("all");

  const rows = useMemo(() => {
    return data.filter((it) => {
      if (availability === "available" && !isAvailable(it.status)) return false;
      if (availability === "unavailable" && !isUnavailable(it.status)) return false;
      const hay = `${it.product_id} ${buildName(it)} ${it.product_type} ${it.ubicacion_id}`.toLowerCase();
      return hay.includes(search.toLowerCase());
    });
  }, [data, search, availability]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <LiveHeader
        title="Catálogo (en vivo)"
        subtitle="Inventario_Plantas desde Google Sheets"
        count={data.length}
        loading={loading}
        onRefresh={refresh}
        testid="live-catalog"
      >
        <div className="flex items-center gap-1 rounded-lg border border-border p-1 bg-card">
          <Button
            type="button"
            size="sm"
            variant={availability === "all" ? "default" : "ghost"}
            onClick={() => setAvailability("all")}
            data-testid="catalog-filter-all"
          >
            Todos
          </Button>
          <Button
            type="button"
            size="sm"
            variant={availability === "available" ? "default" : "ghost"}
            onClick={() => setAvailability("available")}
            data-testid="catalog-filter-available"
          >
            Disponibles
          </Button>
          <Button
            type="button"
            size="sm"
            variant={availability === "unavailable" ? "default" : "ghost"}
            onClick={() => setAvailability("unavailable")}
            data-testid="catalog-filter-unavailable"
          >
            No disponibles / pérdida
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar planta…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-48" data-testid="catalog-search" />
        </div>
      </LiveHeader>

      <StateBlock loading={loading} error={error} empty={!loading && !error && rows.length === 0} onRetry={refresh}>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left">
              <tr className="text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3">Producto</th>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3 text-right">Costo</th>
                <th className="px-4 py-3 text-right">P. sugerido</th>
                <th className="px-4 py-3 text-right">P. venta</th>
                <th className="px-4 py-3">Ubicación</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((it, i) => (
                <tr key={it.product_id || i} className="hover:bg-secondary/30" data-testid={`catalog-row-${it.product_id}`}>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    <div className="flex items-center gap-3 min-w-[220px]">
                      <div className="h-11 w-11 rounded-lg overflow-hidden bg-secondary shrink-0 flex items-center justify-center">
                        {it.image_url ? (
                          <img src={it.image_url} alt={buildName(it)} className="h-full w-full object-cover" />
                        ) : (
                          <Leaf className="h-4 w-4 text-accent" />
                        )}
                      </div>
                      <span>{buildName(it)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{cell(it.product_id)}</td>
                  <td className="px-4 py-3">{cell(it.product_type)}</td>
                  <td className="px-4 py-3"><Badge value={it.status} /></td>
                  <td className="px-4 py-3 text-right font-mono">{cell(it.stock_qty)}</td>
                  <td className="px-4 py-3 text-right font-mono">{crc(it.costo_unitario)}</td>
                  <td className="px-4 py-3 text-right font-mono">{crc(it.precio_sugerido)}</td>
                  <td className="px-4 py-3 text-right font-mono">{crc(it.precio_venta)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{cell(it.ubicacion_id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Mostrando {rows.length} de {data.length}</p>
      </StateBlock>
    </div>
  );
}
